import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["id", "onDragStart", "onDragUpdate", "onDragEnd", "spacing", "items", "getItemId", "itemRenderer"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.replace.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import React, { memo, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { StackWrapper, StackItem } from 'components/Stack';
import useUniqueId from 'hooks/useUniqueId';
export var HTML_ROOT_DRAG_ACTIVE_CLASS = 'data-drag-in-progress';
function getAxisLockedStyles(style) {
  if (style !== null && style !== void 0 && style.transform) {
    // replace the entire part of style.transform up to and including the
    // first comma, essentially isolating the y value in the translate function.
    var axisLockY = "translate(0px, ".concat(style.transform.replace(/^.*?,/, ''));
    return _objectSpread(_objectSpread({}, style), {}, {
      transform: axisLockY
    });
  }
  return style;
}
var Items = /*#__PURE__*/memo(function (props) {
  var items = props.items,
    getItemId = props.getItemId,
    itemRenderer = props.itemRenderer;
  return /*#__PURE__*/React.createElement(React.Fragment, null, items.map(function (item, index) {
    return /*#__PURE__*/React.createElement(Draggable, {
      key: getItemId(item),
      draggableId: getItemId(item),
      index: index
    }, function (_ref, _ref2) {
      var innerRef = _ref.innerRef,
        draggableProps = _ref.draggableProps,
        dragHandleProps = _ref.dragHandleProps;
      var isDragging = _ref2.isDragging;
      return /*#__PURE__*/React.createElement(StackItem, _extends({
        ref: innerRef,
        allowUnknownProps: true
      }, draggableProps, {
        style: getAxisLockedStyles(draggableProps.style)
      }), itemRenderer({
        item: item,
        dragHandleProps: dragHandleProps,
        isDragging: isDragging
      }));
    });
  }));
});
Items.displayName = 'Items';
function getPosition(dragTarget) {
  if (!dragTarget) return null;
  return dragTarget.index + 1;
}
function SortableStack(_ref3) {
  var id = _ref3.id,
    onDragStart = _ref3.onDragStart,
    onDragUpdate = _ref3.onDragUpdate,
    onDragEnd = _ref3.onDragEnd,
    spacing = _ref3.spacing,
    items = _ref3.items,
    getItemId = _ref3.getItemId,
    itemRenderer = _ref3.itemRenderer,
    otherProps = _objectWithoutProperties(_ref3, _excluded);
  var uniqueId = useUniqueId(id);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDragInProgress = _useState2[0],
    setIsDragInProgress = _useState2[1];
  useLayoutEffect(function () {
    // Add an attribute to the <html> element to disable unwanted pointer
    // interactions during drag
    if (isDragInProgress) {
      document.documentElement.setAttribute(HTML_ROOT_DRAG_ACTIVE_CLASS, 'true');
      return function () {
        document.documentElement.removeAttribute(HTML_ROOT_DRAG_ACTIVE_CLASS);
      };
    } else {
      document.documentElement.removeAttribute(HTML_ROOT_DRAG_ACTIVE_CLASS);
    }
  }, [isDragInProgress]);
  function dragStart(drag, provided) {
    onDragStart === null || onDragStart === void 0 ? void 0 : onDragStart(drag, provided);
    setIsDragInProgress(true);
    provided.announce(t('drag_drop_drag_started', {
      position: getPosition(drag.source),
      total: items.length
    }));
  }
  function dragUpdate(drag, provided) {
    onDragUpdate === null || onDragUpdate === void 0 ? void 0 : onDragUpdate(drag, provided);
    var position = getPosition(drag.destination);
    if (position) {
      provided.announce(t('drag_drop_dragging', {
        position: position,
        startPosition: getPosition(drag.source),
        total: items.length
      }));
    } else {
      provided.announce(t('drag_drop_dragging_outside_area'));
    }
  }
  function dragEnd(drag, provided) {
    onDragEnd === null || onDragEnd === void 0 ? void 0 : onDragEnd(drag, provided);
    setIsDragInProgress(false);
    var startPosition = getPosition(drag.source);
    var endPosition = getPosition(drag.destination);
    if (drag.reason === 'CANCEL') {
      provided.announce(t('drag_drop_drag_cancelled', {
        startPosition: startPosition
      }));
    } else if (endPosition) {
      provided.announce(t('drag_drop_drag_ended', {
        startPosition: startPosition,
        endPosition: endPosition,
        total: items.length
      }));
    } else {
      provided.announce(t('drag_drop_drag_ended_outside_area', {
        startPosition: startPosition
      }));
    }
  }
  return /*#__PURE__*/React.createElement(DragDropContext, {
    onDragStart: dragStart,
    onDragUpdate: dragUpdate,
    onDragEnd: dragEnd,
    dragHandleUsageInstructions: t('drag_drop_instructions')
  }, /*#__PURE__*/React.createElement(Droppable, {
    droppableId: uniqueId
  }, function (provided) {
    return /*#__PURE__*/React.createElement(StackWrapper, _extends({
      ref: provided.innerRef,
      as: "list",
      spacing: spacing
    }, otherProps, provided.droppableProps), /*#__PURE__*/React.createElement(Items, {
      items: items,
      getItemId: getItemId,
      itemRenderer: itemRenderer
    }), provided.placeholder);
  }));
}
export { default as DragHandle, DRAG_HANDLE_HOVER_CLASSNAME } from './DragHandle';
export default SortableStack;